/**
 * About Us Page - Redesigned for logistics theme and agency UI standards
 */
import React from 'react';
import { motion } from 'framer-motion';
import { FaFemale, FaUserTie, FaGlobeAsia, FaShieldAlt } from 'react-icons/fa';
import SEO from '../components/ui/SEO';
import { getOrganizationSchema, getBreadcrumbSchema } from '../utils/schema';
import AnimatedSection, { StaggerContainer, StaggerItem } from '../components/ui/AnimatedSection';
import SectionHeader from '../components/ui/SectionHeader';
import HissenPartnership from '../components/sections/HissenPartnership';
import ContactCTA from '../components/sections/ContactCTA';

const pillars = [
  {
    icon: FaFemale,
    title: 'Women Empowered Entity',
    subtitle: 'Women Led Organisation',
    description: 'Proudly a women lead organisation, driving diversity, empowerment and inclusion in maritime services. We believe in setting new benchmarks in the logistics industry.',
    color: 'var(--orange)',
    highlight: true
  },
  {
    icon: FaUserTie,
    title: 'Experienced Management Team',
    subtitle: 'Expert Leadership',
    description: 'Decades of collective experience in shipping, ports, logistics and supply chain operations. Our team brings proven maritime expertise and a track record of operational safety.',
    color: 'var(--orange)'
  },
  {
    icon: FaGlobeAsia,
    title: 'Global Network & Partnerships',
    subtitle: 'Worldwide Connections',
    description: 'Established relationships with shipping lines, port trusts, and global logistical agencies. Our network ensures smooth cargo transit across continents.',
    color: 'var(--orange)'
  },
  {
    icon: FaShieldAlt,
    title: 'Quality, Safety & Compliance',
    subtitle: 'Uncompromising Standards',
    description: 'Adhering strictly to international compliance codes, port safety guidelines, and statutory regulations. We protect your shipments and cargo integrity.',
    color: 'var(--orange)'
  }
];

const values = [
  { title: 'Excellence', desc: 'Delivering safe operations and cargo turnaround speeds.' },
  { title: 'Integrity', desc: 'Transparent reporting, ethical operations, and absolute trust.' },
  { title: 'Innovation', desc: 'Implementing clean green energy systems and modern logistical frameworks.' },
  { title: 'Global Standards', desc: 'Operating in accordance with worldwide maritime and safety guidelines.' }
];

const About = () => {
  const breadcrumbs = getBreadcrumbSchema([
    { name: 'Home', url: 'https://www.isaacgroup.net/' },
    { name: 'About Us', url: 'https://www.isaacgroup.net/about' }
  ]);

  return (
    <main style={{ paddingTop: '72px' }}>
      <SEO 
        title="About ISAAC Logistic Solutions | Shipping Experts in Thoothukudi"
        description="Learn about ISAAC Logistic Solutions India Pvt. Ltd., a trusted women-led entity logistics and shipping agency in Thoothukudi, Tamil Nadu, committed to operational excellence and global standards."
        canonicalUrl="https://www.isaacgroup.net/about"
        schema={[getOrganizationSchema(), breadcrumbs]}
      />
      {/* Page Hero */}
      <section style={{
        padding: '4.5rem 0',
        background: 'var(--navy-deep)',
        position: 'relative',
        overflow: 'hidden',
        borderBottom: '1px solid var(--orange-border)'
      }}>
      {/* Decorative vertical lines */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0, left: '15%', width: '1px',
        background: 'rgba(255,255,255,0.02)', pointerEvents: 'none'
      }} />
      <div style={{
        position: 'absolute', top: 0, bottom: 0, left: '85%', width: '1px',
        background: 'rgba(255,255,255,0.02)', pointerEvents: 'none'
      }} />

      <div className="container-xxl px-4 px-lg-5" style={{ position: 'relative', zIndex: 1 }}>
        <AnimatedSection variant="fadeUp">
          <span className="section-tag">Who We Are</span>
          <h1 style={{ color: '#fff', marginTop: '0.75rem', marginBottom: '1.2rem', letterSpacing: '-0.025em', fontWeight: 800 }}>
            About <span style={{ color: 'var(--orange)' }}>ISAAC Group</span>
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.5)', maxWidth: '600px', lineHeight: '1.8', fontSize: '1rem', margin: 0, textAlign: 'justify' }}>
            An entity logistics provider delivering integrated shipping, ports management, and marine electrification solutions with absolute integrity.
          </p>
        </AnimatedSection>
      </div>
    </section>

    {/* Company Overview */}
    <section style={{ padding: '4.5rem 0', background: '#FFFFFF', borderBottom: '1px solid rgba(10,17,40,0.05)' }}>
      <div className="container-xxl px-4 px-lg-5">
        <div className="row align-items-start g-5">
          {/* Left Side */}
          <div className="col-lg-6">
            <AnimatedSection variant="fadeLeft">
              <span className="section-tag">Overview</span>
              <h2 className="section-title mt-2" style={{ fontWeight: 800, fontSize: 'clamp(1.8rem, 4vw, 2.4rem)' }}>
                ISAAC Logistic Solutions India Pvt. Ltd.
              </h2>
              <div className="section-divider" style={{ width: '40px', background: 'var(--orange)', height: '1px', margin: '1.5rem 0' }} />
              <p style={{ color: 'var(--text-body)', lineHeight: '1.8', marginBottom: '1.5rem', fontSize: '0.95rem', textAlign: 'justify' }}>
                ISAAC Logistic Solutions India Pvt. Ltd. is a premier logistics agency offering structured shipping and marine operations across key subcontinent trade ports. With our primary operations focused on the major hubs of <strong style={{ color: 'var(--orange)' }}>Thoothukudi</strong>, <strong style={{ color: 'var(--purple-accent-light)' }}>Vizhinjam</strong>, and <strong style={{ color: 'var(--teal-light)' }}>Chennai</strong>, we deliver localized expertise, dedicated vessel husbanding, and comprehensive customs brokerage. As a women-led organization, we champion operational leadership, compliance, and technological integration.
              </p>
              <p style={{ color: 'var(--text-body)', lineHeight: '1.8', marginBottom: '1.5rem', fontSize: '0.95rem', textAlign: 'justify' }}>
                Our operations range from full-vessel husbanding and agency representation to heavy lift project cargo movements and green port energy transitions in partnership with international technology providers.
              </p>
              <p style={{ color: 'var(--text-body)', lineHeight: '1.8', fontSize: '0.95rem', margin: 0, textAlign: 'justify' }}>
                We prioritize absolute safety, transparent reporting, and port compliance to ensure that our clients’ supply chains remain uninterrupted and robust.
              </p>
            </AnimatedSection>
          </div>

          {/* Right Side */}
          <div className="col-lg-6">
            <AnimatedSection variant="fadeRight" delay={0.1}>
              <div style={{
                background: 'var(--bg-dark)',
                borderRadius: 2,
                padding: '3rem 2.5rem',
                border: '1px solid rgba(255,255,255,0.05)',
                boxShadow: 'var(--shadow-lg)'
              }}>
                <h4 style={{
                  fontFamily: 'var(--font-heading)',
                  color: 'var(--orange)',
                  fontWeight: 700,
                  marginBottom: '1.2rem',
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  letterSpacing: '2px'
                }}>
                  Our Mission Statement
                </h4>
                <p style={{ color: '#FFFFFF', lineHeight: '1.8', marginBottom: '3rem', fontSize: '0.98rem', textAlign: 'justify' }}>
                  To deliver integrated shipping, logistics and marine solutions with excellence, integrity and global standards — connecting businesses worldwide through reliable, innovative, and sustainable logistics.
                </p>
                
                {/* Typographic values table (no emojis) */}
                <h5 style={{
                  fontFamily: 'var(--font-heading)',
                  color: 'rgba(255,255,255,0.35)',
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  textTransform: 'uppercase',
                  letterSpacing: '1.5px',
                  marginBottom: '1.5rem',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                  paddingBottom: '0.5rem'
                }}>Core Operational Values</h5>

                <div className="row g-4">
                  {values.map((v, i) => (
                    <div key={i} className="col-sm-6">
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.2rem'
                      }}>
                        <h6 style={{ color: '#fff', fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.82rem', margin: 0 }}>
                          <span style={{ color: 'var(--orange)', marginRight: '0.4rem' }}>•</span>{v.title}
                        </h6>
                        <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', lineHeight: '1.5', margin: 0 }}>{v.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </section>

    {/* Pillars section */}
    <section style={{ padding: '4.5rem 0', background: 'var(--bg-page)', borderBottom: '1px solid rgba(10,17,40,0.05)' }}>
      <div className="container-xxl px-4 px-lg-5">
        <SectionHeader
          tag="Our Operational Pillars"
          title="What Defines ISAAC"
          subtitle="Four core principles that support every service we deliver and every partnership we build."
        />
        
        <StaggerContainer className="row g-4" staggerDelay={0.08}>
          {pillars.map((p, i) => {
            const IconComp = p.icon;
            return (
              <StaggerItem key={i} className="col-md-6">
                <motion.div
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.25 }}
                  style={{
                    background: '#FFFFFF',
                    borderRadius: 2,
                    padding: '2.5rem',
                    height: '100%',
                    border: p.highlight ? '1px solid var(--purple-accent-border)' : '1px solid rgba(10,17,40,0.04)',
                    borderLeft: p.highlight ? '4px solid var(--purple-accent)' : '1px solid rgba(10,17,40,0.04)',
                    boxShadow: p.highlight ? 'var(--shadow-md)' : 'var(--shadow-sm)',
                    transition: 'border-color 0.2s, box-shadow 0.2s'
                  }}
                  className="about-pillar-card"
                >
                  <div className="d-flex align-items-start gap-4">
                    <div
                      style={{
                        width: '42px', height: '42px', borderRadius: 2,
                        background: p.highlight ? 'var(--purple-accent-dim)' : 'var(--orange-dim)',
                        border: p.highlight ? '1px solid var(--purple-accent-border)' : '1px solid var(--orange-border)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, marginTop: '2px'
                      }}
                    >
                      <IconComp size={16} color={p.highlight ? 'var(--purple-accent-light)' : 'var(--orange)'} />
                    </div>
                    <div style={{ flexGrow: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
                        <span style={{
                          color: p.highlight ? 'var(--purple-accent-light)' : 'var(--orange)',
                          fontSize: '0.62rem',
                          fontWeight: 700,
                          letterSpacing: '1.5px',
                          textTransform: 'uppercase'
                        }}>
                          {p.subtitle}
                        </span>
                        {p.highlight && (
                          <span style={{
                            background: 'var(--purple-accent-dim)',
                            border: '1px solid var(--purple-accent-border)',
                            borderRadius: '30px',
                            padding: '0.15rem 0.5rem',
                            fontSize: '0.55rem',
                            fontWeight: '700',
                            color: 'var(--purple-accent)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            Certified
                          </span>
                        )}
                      </div>
                      <h4 style={{
                        fontFamily: 'var(--font-heading)',
                        fontWeight: 700,
                        color: 'var(--navy)',
                        fontSize: '1.05rem',
                        marginBottom: '0.6rem',
                        letterSpacing: '-0.015em'
                      }}>
                        {p.title}
                      </h4>
                      <p style={{ color: 'var(--text-body)', fontSize: '0.85rem', lineHeight: '1.7', margin: 0, textAlign: 'justify' }}>
                        {p.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      </div>
    </section>

    <HissenPartnership />
    <ContactCTA />

    <style>{`
      .about-pillar-card:hover {
        border-color: var(--orange) !important;
      }
    `}</style>
  </main>
  );
};

export default About;
